import { Button, Toast } from "@ebsco-ui/ebsco-ui";
import { FORM_ERROR } from "final-form";
import createDecorator from "final-form-focus";
import PropTypes from "prop-types";
import React from "react";
import { Form } from "react-final-form";
import { FormattedMessage, useIntl } from "react-intl";
import { connect } from "react-redux";
import sharedMessages from "../../../../shared-messages";
import {
  getIsSso,
  getUserInfo,
  savePersonalUserFailure,
  savePersonalUserRequest,
  savePersonalUserSuccess
} from "../../../store/personal-user";
import submitErrorFieldsHaveChanged from "../../../utils/submit-error-fields-have-changed";
import CoppaStrategy from "../../coppa-strategy/coppa-strategy";
import { useEdgeApiContext } from "../../edge-api";
import FormErrorNotification from "../../form-error-notification";
import {
  CoppaSsoUpdateUserFormFields,
  CoppaUpdateUserFormFields,
  NonCoppaUpdateUserFormFields,
  SsoUpdateUserFormFields
} from "../update-user-form-types";
import messages from "./update-user-form-messages";

import "./update-user-form.scss";

const focusOnErrors = createDecorator();

const UpdateUserForm = props => {
  const {
    userInfo: { primaryEmail, givenName, familyName, coppa: isCoppa },
    isSso,
    savePersonalUserSuccess,
    savePersonalUserFailure
  } = props;

  const { formatMessage } = useIntl();
  const personalUserApi = useEdgeApiContext();

  const CoppaComponent = isSso
    ? CoppaSsoUpdateUserFormFields
    : CoppaUpdateUserFormFields;

  const NonCoppaComponent = isSso
    ? SsoUpdateUserFormFields
    : NonCoppaUpdateUserFormFields;

  const onSubmit = async data => {
    let formErrors;

    const { familyName = "", givenName = "", primaryEmail = "" } = data;
    try {
      const body = { ...data, familyName, givenName, primaryEmail };
      await personalUserApi.putPersonalUser(body);
      savePersonalUserSuccess(body);
      Toast(formatMessage(messages.updatedAccount), {
        closeButton: false,
        type: "success"
      });
    } catch (error) {
      savePersonalUserFailure(error);
      formErrors = {
        [FORM_ERROR]: {
          headerMessage: formatMessage(sharedMessages.somethingWentWrong),
          descriptionMessage: formatMessage(messages.unableToUpdateAccount)
        }
      };
      if (error.response?.status === 400) {
        formErrors = {
          primaryEmail: [formatMessage(sharedMessages.preexistingEmailAddress)]
        };
      }
    }

    return formErrors;
  };

  return (
    <div className="update__user-form">
      <Form
        onSubmit={formValues =>
          onSubmit({
            ...formValues,
            ...(isSso && { isUpdatedBySSOUser: isSso })
          })
        }
        initialValues={{
          givenName,
          familyName,
          primaryEmail,
          isCoppa
        }}
        decorators={[focusOnErrors]}
      >
        {({
          handleSubmit,
          submitError,
          submitting,
          submitFailed,
          submitErrors,
          dirtyFieldsSinceLastSubmit,
          dirty
        }) => {
          const disableSave =
            submitting ||
            !(submitFailed
              ? submitErrorFieldsHaveChanged(
                  submitErrors,
                  dirtyFieldsSinceLastSubmit
                )
              : dirty);

          return (
            <form onSubmit={handleSubmit}>
              {submitError && <FormErrorNotification {...submitError} />}
              <CoppaStrategy
                coppaComponent={CoppaComponent}
                nonCoppaComponent={NonCoppaComponent}
              />
              <Button
                className="save-button"
                type="submit"
                disabled={disableSave}
                styleType="solid"
              >
                <FormattedMessage {...sharedMessages.save} />
              </Button>
            </form>
          );
        }}
      </Form>
    </div>
  );
};

UpdateUserForm.defaultProps = {
  userInfo: {
    familyName: ""
  }
};

UpdateUserForm.propTypes = {
  userInfo: PropTypes.shape({
    primaryEmail: PropTypes.string.isRequired,
    givenName: PropTypes.string.isRequired,
    familyName: PropTypes.string,
    coppa: PropTypes.bool
  }),
  savePersonalUserSuccess: PropTypes.func.isRequired,
  savePersonalUserFailure: PropTypes.func.isRequired,
  savePersonalUserRequest: PropTypes.func,
  isSso: PropTypes.bool
};

const mapStateToProps = state => ({
  userInfo: getUserInfo(state),
  isSso: getIsSso(state)
});

const mapDispatchToProps = {
  savePersonalUserRequest,
  savePersonalUserSuccess,
  savePersonalUserFailure
};

export { UpdateUserForm };
export default connect(mapStateToProps, mapDispatchToProps)(UpdateUserForm);
